import { Capacitor, registerPlugin } from "@capacitor/core";

// Proxies to the native @exxili/capacitor-nfc module (registered as "NFC").
// We avoid importing the plugin's JS wrapper so the web build never resolves it.
const NfcNative = registerPlugin("NFC");

export function isNativeApp() {
  try {
    return !!(Capacitor && Capacitor.isNativePlatform && Capacitor.isNativePlatform());
  } catch (_) {
    return false;
  }
}

function decodeB64Payload(b64) {
  try {
    const bin = atob(b64);
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    // Strip NDEF status/lang bytes best-effort, keep printable ASCII.
    return new TextDecoder("utf-8").decode(bytes).replace(/[^\x20-\x7E]+/g, " ").trim();
  } catch (_) {
    return "";
  }
}

// Reads one NFC tag and resolves the best value (NDEF payload, else tag UID).
// Native on iOS/Android, Web NFC fallback in the browser (Chrome Android).
export async function scanNfc() {
  if (isNativeApp()) {
    return await new Promise((resolve, reject) => {
      let done = false;
      let tagHandle;
      let errHandle;
      const cleanup = () => {
        try { tagHandle && tagHandle.remove && tagHandle.remove(); } catch (_) {}
        try { errHandle && errHandle.remove && errHandle.remove(); } catch (_) {}
      };
      NfcNative.addListener("nfcTag", (data) => {
        if (done) return;
        done = true;
        let val = "";
        try {
          const rec = data && data.messages && data.messages[0] && data.messages[0].records && data.messages[0].records[0];
          if (rec && rec.payload) val = decodeB64Payload(rec.payload);
          if (!val) val = (data && data.tagInfo && data.tagInfo.uid) || "";
        } catch (_) {
          val = (data && data.tagInfo && data.tagInfo.uid) || "";
        }
        cleanup();
        resolve(val);
      }).then((h) => { tagHandle = h; });
      NfcNative.addListener("nfcError", () => {
        if (done) return;
        done = true;
        cleanup();
        reject(new Error("nfc-read-failed"));
      }).then((h) => { errHandle = h; });
      Promise.resolve(NfcNative.startScan ? NfcNative.startScan({}) : null).catch(() => {
        if (done) return;
        done = true;
        cleanup();
        reject(new Error("nfc-read-failed"));
      });
    });
  }

  if (!("NDEFReader" in window)) throw new Error("nfc-unsupported");
  const reader = new window.NDEFReader();
  await reader.scan();
  return await new Promise((resolve, reject) => {
    reader.onreadingerror = () => reject(new Error("nfc-read-failed"));
    reader.onreading = (ev) => {
      let value = ev.serialNumber || "";
      for (const rec of ev.message.records) {
        try {
          if (rec.recordType === "url" || rec.recordType === "text") {
            value = new TextDecoder(rec.encoding || "utf-8").decode(rec.data);
            break;
          }
        } catch (_) {}
      }
      resolve(value);
    };
  });
}
